import { ArrowLeftOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { Link } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import ProposalForm from "../ProposalAddPage/components/ProposalForm.tsx"
import type { ProposalFormData } from "../ProposalAddPage/components/proposal-form-data.tsx"
import {
  fetchProposal,
  generateProposal,
  getProposalPreview,
  updateProposal as updateProposalThunk
} from "../redux/proposals.thunks.ts"
import type { ProposalRequestData } from "../redux/proposals.thunks.types.ts"
import { ProposalEditPreviewHTML } from "./components/ProposalEditPreviewHTML.tsx"
import { useCallbackRef } from "../../../hooks/use-callback-ref.ts"

function ProposalEditPage({
  getProposal,
  updateProposal,
  generateProposal,
  createProposalPreview,
  proposal,
  saving,
  fetching
}: ConnectedProps<typeof connector>) {
  const id = useParams()?.id as string
  const navigate = useNavigate()
  const [previewHTML, setPreviewHTML] = useState<string | null>(null)
  const [formData, setFormData] = useState<ProposalRequestData | null>(null)

  const hasPDF = !!proposal?.attachments?.length

  const getProposalLatest = useCallbackRef(getProposal)
  useEffect(() => {
    if (!id) return

    getProposalLatest(id)
  }, [id, getProposalLatest])

  useEffect(() => {
    generateDocumentTitle("Edit Proposal")
  }, [])

  if (fetching) {
    return <Skeleton.Node className={"size-full"} />
  }

  if (!proposal) {
    return (
      <Empty
        className={"mx-auto"}
        description={"The proposal was not found."}
      />
    )
  }

  const handleUpdate = async (data: Partial<ProposalFormData>) => {
    const params: ProposalRequestData = {
      ...data,
      // lead_id: proposal?.lead_id, // EZB-1759
      proposal_id: proposal?.id
    }
    setFormData(params)
    const html = await createProposalPreview(params)?.unwrap()
    if (html) setPreviewHTML(html)
  }

  const handleGenerate = () => {
    if (!formData) return

    return generateProposal({
      data: formData,
      navigate,
      leadId: proposal?.lead_id,
      proposalId: proposal?.id
    })
  }

  const handleConfirm = async () => {
    if (!formData) return
    const proposalId = proposal?.id

    try {
      if (hasPDF) {
        await updateProposal({ data: formData, proposalId }).unwrap()
        await handleGenerate()
      } else {
        await updateProposal({
          data: formData,
          proposalId,
          showSuccessMessage: true
        }).unwrap()
        navigate(RoutesPaths.lead.view(proposal?.lead_id))
      }
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <div className={"grid"}>
      {previewHTML && (
        <ProposalEditPreviewHTML
          html={previewHTML}
          saving={saving}
          onClickConfirm={handleConfirm}
          onClickGenerate={handleGenerate}
          onClose={() => setPreviewHTML(null)}
        />
      )}

      <Flex
        vertical
        gap={"small"}
        className={"max-h-[calc(100vh-90px)] overflow-auto"}
      >
        <Flex
          align={"center"}
          justify={"space-between"}
          className={"sticky top-0 z-10 bg-background"}
        >
          <Typography.Title level={2}>Edit Proposal</Typography.Title>
          <Link to={RoutesPaths.lead.view(proposal?.lead_id)}>
            <Button icon={<ArrowLeftOutlined />}>Back to Lead</Button>
          </Link>
        </Flex>
        <ProposalForm
          saving={saving}
          onSubmit={handleUpdate}
          checkDirty={!previewHTML}
          projectTypes={proposal?.project_type_details}
          initialValues={
            {
              ...proposal?.proposal_data,
              company: {
                ...(proposal?.proposal_data?.company || {}),
                logo_url:
                  proposal?.logo_url ||
                  proposal?.proposal_data?.company?.logo_url
              },
              visible_blocks: proposal?.visible_blocks
            } as ProposalFormData
          }
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  proposal: state.proposals.proposal,
  saving: state.proposals.saveFetching,
  fetching: state.proposals.fetching
})

const mapDispatchToProps = {
  createProposalPreview: getProposalPreview,
  generateProposal: generateProposal,
  getProposal: fetchProposal,
  updateProposal: updateProposalThunk
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProposalEditPage)
